<template>
  <div class="tc-page py-3">
    <header class="tc-toolbar">
      <div class="tc-toolbar__title">
        <span class="tc-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Customers</h1>
          <p>Explore every customer — filter by crop, animal, medicine, tag &amp; more, then call or view history</p>
        </div>
      </div>
      <div class="tc-toolbar__actions">
        <span v-if="lastUpdatedAt && !isLoading" class="tc-updated">Updated {{ lastUpdatedLabel }}</span>
        <CButton size="sm" class="tc-refresh-btn" :class="{ 'is-loading': isLoading }" :disabled="isLoading"
          @click="reload">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" class="tc-refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="tc-alert">{{ errorMessage }}</CAlert>

    <section class="tc-section">
      <header class="tc-section__head">
        <h2>{{ isFilterOrSearchActive ? 'Filtered Customers' : 'All Customers' }}</h2>
        <span v-if="activeFilterChips.length" class="tc-active-count">{{ activeFilterChips.length }} filter{{ activeFilterChips.length === 1 ? '' : 's' }} active</span>
      </header>

      <div class="tc-section__body">
        <div class="tc-filters">
          <div class="tc-filter tc-filter--search">
            <label class="tc-filter__label">Search</label>
            <form class="tc-search-wrap" @submit.prevent="onSearchSubmit">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" class="tc-search-icon" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input v-model.trim="searchTerm" type="text" class="tc-input"
                placeholder="Search by name or phone…" />
              <button v-if="searchTerm" type="button" class="tc-search-clear" aria-label="Clear search"
                @click="searchTerm = ''">×</button>
            </form>
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">Customer Tag</label>
            <AppDropdown v-model="filters.customer_type" :options="customerTypeOptions" placeholder="All tags" />
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">Crop</label>
            <AppDropdown v-model="filters.crop" :options="cropOptions" searchable
              search-placeholder="Search crop…" placeholder="All crops" />
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">Animal</label>
            <AppDropdown v-model="filters.animal" :options="animalOptions" searchable
              search-placeholder="Search animal…" placeholder="All animals" />
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">Medicine / Product</label>
            <AppDropdown v-model="filters.product" :options="productOptions" searchable
              search-placeholder="Search product…" placeholder="All products" />
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">State</label>
            <AppDropdown v-model="filters.state" :options="stateOptions" searchable
              search-placeholder="Search state…" placeholder="All states" />
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">District</label>
            <AppDropdown v-model="filters.district" :options="districtOptions" searchable
              search-placeholder="Search district…" placeholder="All districts" />
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">Call Type</label>
            <AppDropdown v-model="filters.call_type" :options="callTypeOptions" searchable
              search-placeholder="Search call type…" placeholder="All call types" />
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">Call Status</label>
            <AppDropdown v-model="filters.call_status" :options="callStatusOptions" searchable
              search-placeholder="Search call status…" placeholder="All call statuses" />
          </div>

          <div class="tc-filter tc-filter--range">
            <label class="tc-filter__label">Date Range</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">Date Applies To</label>
            <AppDropdown v-model="filters.date_basis" :options="dateBasisOptions" placeholder="Order or call" />
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">Order Activity</label>
            <AppDropdown v-model="filters.activity" :options="activityOptions" placeholder="Any activity" />
          </div>

          <div class="tc-filter">
            <label class="tc-filter__label">Bulk Customers</label>
            <AppDropdown v-model="filters.bulk" :options="bulkOptions" placeholder="All customers" />
          </div>

          <div class="tc-filter tc-filter--bottles">
            <label class="tc-filter__label">Bottle Count</label>
            <div class="tc-bottle-range">
              <input v-model.trim="filters.bottles_min" type="number" min="0" inputmode="numeric"
                class="tc-input tc-input--num" placeholder="Min" @keyup.enter="applyFilters" />
              <span class="tc-bottle-range__sep">–</span>
              <input v-model.trim="filters.bottles_max" type="number" min="0" inputmode="numeric"
                class="tc-input tc-input--num" placeholder="Max" @keyup.enter="applyFilters" />
            </div>
          </div>

          <div class="tc-filter-actions">
            <button type="button" class="tc-apply-btn" :disabled="isLoading" @click="applyFilters">Apply</button>
            <button type="button" class="tc-reset-btn" :disabled="isLoading || !isFilterOrSearchActive"
              @click="clearAll">Reset</button>
          </div>
        </div>

        <div v-if="activeFilterChips.length" class="tc-chips">
          <span v-for="chip in activeFilterChips" :key="chip.key" class="tc-chip">
            <span class="tc-chip__label">{{ chip.label }}</span>
            <button type="button" class="tc-chip__x" aria-label="Remove filter" @click="removeChip(chip.key)">×</button>
          </span>
        </div>
      </div>

      <div class="tc-table-region">
        <BaseTable
          title="Results"
          :items="tableItems"
          :columns="columns"
          :loading="isLoading"
          :sortable="false"
          :show-count="false"
          empty-text="No customers match these filters."
        >
          <template #toolbar>
            <span class="tc-result-hint">{{ pageRangeLabel }}</span>
          </template>

          <template #cell-customer="{ row }">
            <div class="tc-customer-cell">
              <div class="tc-avatar" :style="{ background: avatarColor(row) }">{{ initials(row.name) }}</div>
              <div class="tc-customer-id">
                <div class="tc-customer-name">{{ row.name || 'Unnamed' }}</div>
                <div v-if="row.id" class="tc-customer-sub">#{{ row.id }}</div>
              </div>
            </div>
          </template>

          <template #cell-phone="{ row }">
            <span v-if="row.phone" class="tc-phone">{{ row.phone }}</span>
            <span v-else class="tc-dim">—</span>
          </template>

          <template #cell-location="{ row }">
            <span v-if="districtLabel(row)" class="tc-pill tc-pill--loc">{{ districtLabel(row) }}</span>
            <span v-else class="tc-dim">—</span>
          </template>

          <template #cell-tags="{ row }">
            <div class="tc-tagwrap">
              <span v-if="customerTypeLabel(row)" class="tc-pill tc-pill--type">{{ customerTypeLabel(row) }}</span>
              <span v-if="Number(row.bulk_orders_count) > 0" class="tc-pill tc-pill--bulk">Bulk</span>
              <span v-for="crop in cropChips(row)" :key="`crop-${crop}`" class="tc-pill tc-pill--crop">{{ crop }}</span>
              <span v-for="animal in animalChips(row)" :key="`an-${animal}`" class="tc-pill tc-pill--animal">{{ animal }}</span>
              <span v-if="extraTagCount(row) > 0" class="tc-pill tc-pill--more">+{{ extraTagCount(row) }}</span>
              <span v-if="!hasAnyTag(row)" class="tc-dim">—</span>
            </div>
          </template>

          <template #cell-actions="{ row }">
            <div class="tc-row-actions">
              <button type="button" class="tc-act tc-act--view" @click="openDetail(row)">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span>History</span>
              </button>
              <button type="button" class="tc-act tc-act--call" :disabled="!row.phone" title="Open in Tele Sales"
                @click="goToTelesales(row.phone)">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span>Call</span>
              </button>
              <button type="button" class="tc-act tc-act--copy" :disabled="!row.phone"
                :title="copiedId === row.id ? 'Copied!' : 'Copy phone'" @click="copyPhone(row.phone, row.id)">
                <svg v-if="copiedId === row.id" viewBox="0 0 24 24" width="13" height="13" fill="none"
                  stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <svg v-else viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              </button>
            </div>
          </template>
        </BaseTable>

        <div v-if="!isLoading && rows.length && lastPage > 1" class="tc-pagination-bar">
          <span class="tc-pagination-info">{{ pageRangeLabel }}</span>
          <AppPager :current-page="currentPage" :last-page="lastPage" @change="onPageChange" />
        </div>
      </div>
    </section>

    <transition name="tc-modal-fade">
      <div v-if="showDetailModal && detailCustomer" class="tc-modal" role="dialog" aria-modal="true"
        aria-labelledby="tc-modal-title" @mousedown.self="closeDetail">
        <div class="tc-modal__panel" @mousedown.stop>
          <header class="tc-modal__head">
            <div class="tc-modal__id">
              <div class="tc-modal__avatar" :style="{ background: avatarColor(detailCustomer) }">{{ initials(detailCustomer.name) }}</div>
              <div class="tc-modal__id-text">
                <h3 id="tc-modal-title" class="tc-modal__name">{{ detailCustomer.name || 'Unnamed' }}</h3>
                <div class="tc-modal__meta">
                  <span v-if="detailCustomer.phone" class="tc-modal__phone">{{ detailCustomer.phone }}</span>
                  <span v-if="districtLabel(detailCustomer)" class="tc-pill tc-pill--loc">{{ districtLabel(detailCustomer) }}</span>
                  <span v-if="customerTypeLabel(detailCustomer)" class="tc-pill tc-pill--type">{{ customerTypeLabel(detailCustomer) }}</span>
                  <span v-if="Number(detailCustomer.bulk_orders_count) > 0" class="tc-pill tc-pill--bulk">Bulk</span>
                </div>
              </div>
            </div>
            <button type="button" class="tc-modal__close" aria-label="Close" @click="closeDetail">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </header>

          <div class="tc-modal__toolbar">
            <button type="button" class="tc-modal__btn tc-modal__btn--call" :disabled="!detailCustomer.phone"
              @click="goToTelesales(detailCustomer.phone)">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <span>Call in Tele Sales</span>
            </button>
            <button type="button" class="tc-modal__btn tc-modal__btn--copy" :disabled="!detailCustomer.phone"
              @click="copyPhone(detailCustomer.phone, detailCustomer.id)">
              <svg v-if="copiedId === detailCustomer.id" viewBox="0 0 24 24" width="15" height="15" fill="none"
                stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <span>{{ copiedId === detailCustomer.id ? 'Copied!' : 'Copy phone' }}</span>
            </button>
          </div>

          <div v-if="cropChips(detailCustomer, 99).length || animalChips(detailCustomer, 99).length" class="tc-modal__profile">
            <div v-if="cropChips(detailCustomer, 99).length" class="tc-modal__profile-row">
              <span class="tc-modal__profile-key">Crops</span>
              <div class="tc-modal__profile-vals">
                <span v-for="crop in cropChips(detailCustomer, 99)" :key="`mc-${crop}`" class="tc-pill tc-pill--crop">{{ crop }}</span>
              </div>
            </div>
            <div v-if="animalChips(detailCustomer, 99).length" class="tc-modal__profile-row">
              <span class="tc-modal__profile-key">Animals</span>
              <div class="tc-modal__profile-vals">
                <span v-for="animal in animalChips(detailCustomer, 99)" :key="`ma-${animal}`" class="tc-pill tc-pill--animal">{{ animal }}</span>
              </div>
            </div>
          </div>

          <div class="tc-modal__body">
            <div v-if="detailLoading" class="tc-modal__loading">
              <CSpinner size="sm" class="mr-2" />
              <span>Loading history…</span>
            </div>
            <div v-else class="tc-history-grid">
              <div class="tc-history-col">
                <h4 class="tc-history-title">Recent Orders</h4>
                <div v-if="detailOrders.length" class="tc-history-list">
                  <div v-for="order in detailOrders" :key="`o-${order.id}`" class="tc-history-item">
                    <div class="tc-history-item__line">
                      <span class="tc-history-item__strong">{{ order.order_number || ('#' + order.id) }}</span>
                      <span class="tc-history-item__amount">{{ formatCurrency(order.grand_total) }}</span>
                    </div>
                    <div class="tc-history-item__sub">
                      {{ orderDateDisplay(order) }}
                      <template v-if="order.payment_status">
                        <span class="tc-dot">·</span>
                        <span :class="paymentStatusClass(order.payment_status)">{{ formatLabel(order.payment_status) }}</span>
                      </template>
                      <template v-if="order.order_status">
                        <span class="tc-dot">·</span>{{ formatLabel(order.order_status) }}
                      </template>
                    </div>
                  </div>
                </div>
                <div v-else class="tc-history-empty">No orders found in this organisation.</div>
              </div>

              <div class="tc-history-col">
                <h4 class="tc-history-title">Recent Calls</h4>
                <div v-if="detailCalls.length" class="tc-history-list">
                  <div v-for="call in detailCalls" :key="`c-${call.id}`" class="tc-history-item">
                    <div class="tc-history-item__line">
                      <span class="tc-history-item__date">{{ formatCallDate(call.created_at) }}</span>
                      <span class="tc-pill tc-pill--call">{{ formatLabel(call.status) }}</span>
                    </div>
                    <div class="tc-history-item__sub">{{ call.caller || 'Unknown caller' }}</div>
                  </div>
                </div>
                <div v-else class="tc-history-empty">No calls found in this organisation.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppPager from '@/components/AppPager.vue'
import BaseTable from '@/components/BaseTable.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'

const PER_PAGE = 15

export default {
  name: 'TelesalesCustomers',
  components: { AppDropdown, AppPager, BaseTable, DateRangePicker },
  data () {
    return {
      rows: [],
      searchTerm: '',
      currentPage: 1,
      lastPage: 1,
      total: 0,
      isLoading: false,
      errorMessage: '',
      lastUpdatedAt: null,
      dateRange: { fromDate: '', toDate: '' },
      filters: {
        customer_type: '',
        crop: '',
        animal: '',
        product: '',
        state: '',
        district: '',
        call_type: '',
        call_status: '',
        activity: '',
        bulk: '',
        bottles_min: '',
        bottles_max: '',
        date_basis: ''
      },
      applied: {
        search: '',
        customer_type: '',
        crop: '',
        animal: '',
        product: '',
        state: '',
        district: '',
        call_type: '',
        call_status: '',
        activity: '',
        bulk: '',
        bottles_min: '',
        bottles_max: '',
        date_from: '',
        date_to: '',
        date_basis: ''
      },
      options: {
        states: [],
        districts: [],
        crops: [],
        animals: [],
        products: [],
        customer_types: [],
        call_types: [],
        call_statuses: []
      },
      columns: [
        { key: 'customer', label: 'Customer', sortable: false },
        { key: 'phone', label: 'Phone', sortable: false },
        { key: 'location', label: 'Location', sortable: false },
        { key: 'tags', label: 'Tags', sortable: false },
        { key: 'orders', label: 'Orders', align: 'right', sortable: false, width: '90px' },
        { key: 'bottles', label: 'Bottles', align: 'right', sortable: false, width: '90px' },
        { key: 'actions', label: '', align: 'right', sortable: false }
      ],
      // Detail modal
      showDetailModal: false,
      detailCustomer: null,
      detailOrders: [],
      detailCalls: [],
      detailLoading: false,
      // Copy-phone affordance
      copiedId: null,
      copiedTimer: null
    }
  },
  beforeDestroy () {
    if (this.copiedTimer) clearTimeout(this.copiedTimer)
  },
  computed: {
    isFilterOrSearchActive () {
      return !!(this.applied.search || this.activeFilterChips.length)
    },
    tableItems () {
      return this.rows.map((row) => ({
        ...row,
        orders: this.ordersCount(row),
        bottles: Number(row.bottles || 0)
      }))
    },
    customerTypeOptions () {
      return [{ value: '', label: 'All tags' }].concat(
        (this.options.customer_types || []).map((t) => ({ value: t.value, label: t.label }))
      )
    },
    cropOptions () {
      return [{ value: '', label: 'All crops' }].concat(
        (this.options.crops || []).map((c) => ({ value: String(c.id), label: c.name }))
      )
    },
    animalOptions () {
      return [{ value: '', label: 'All animals' }].concat(
        (this.options.animals || []).map((a) => ({ value: a, label: a }))
      )
    },
    productOptions () {
      return [{ value: '', label: 'All products' }].concat(
        (this.options.products || []).map((p) => ({ value: p, label: p }))
      )
    },
    stateOptions () {
      return [{ value: '', label: 'All states' }].concat(
        (this.options.states || []).map((s) => ({ value: s, label: s }))
      )
    },
    districtOptions () {
      return [{ value: '', label: 'All districts' }].concat(
        (this.options.districts || []).map((d) => ({ value: d, label: d }))
      )
    },
    callTypeOptions () {
      return [{ value: '', label: 'All call types' }].concat(
        (this.options.call_types || []).map((t) => ({ value: t.value || t.slug, label: t.label || t.name }))
      )
    },
    callStatusOptions () {
      return [{ value: '', label: 'All call statuses' }].concat(
        (this.options.call_statuses || []).map((s) => ({ value: s.value || s.slug, label: s.label || s.name }))
      )
    },
    activityOptions () {
      return [
        { value: '', label: 'Any activity' },
        { value: 'ordered', label: 'Has ordered' },
        { value: 'never', label: 'Never ordered' }
      ]
    },
    dateBasisOptions () {
      return [
        { value: '', label: 'Order or call' },
        { value: 'order', label: 'Order date' },
        { value: 'call', label: 'Call date' }
      ]
    },
    bulkOptions () {
      return [
        { value: '', label: 'All customers' },
        { value: '1', label: 'Bulk only (discounted orders)' }
      ]
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return ''
      const hours = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    summaryCards () {
      const pageOrders = this.rows.reduce((sum, row) => sum + this.ordersCount(row), 0)
      const pageBottles = this.rows.reduce((sum, row) => sum + Number(row.bottles || 0), 0)
      const pageBulk = this.rows.filter((row) => Number(row.bulk_orders_count) > 0).length
      return [
        { key: 'total', label: this.isFilterOrSearchActive ? 'Matches' : 'Total Customers', value: this.total || this.rows.length },
        { key: 'orders', label: 'Orders (Page)', value: pageOrders },
        { key: 'bottles', label: 'Bottles (Page)', value: pageBottles },
        { key: 'bulk', label: 'Bulk (Page)', value: pageBulk }
      ]
    },
    activeFilterChips () {
      const chips = []
      const f = this.applied
      if (f.customer_type) chips.push({ key: 'customer_type', label: this.labelFor(this.customerTypeOptions, f.customer_type) })
      if (f.crop) chips.push({ key: 'crop', label: `Crop: ${this.labelFor(this.cropOptions, f.crop)}` })
      if (f.animal) chips.push({ key: 'animal', label: `Animal: ${f.animal}` })
      if (f.product) chips.push({ key: 'product', label: `Product: ${f.product}` })
      if (f.state) chips.push({ key: 'state', label: `State: ${f.state}` })
      if (f.district) chips.push({ key: 'district', label: `District: ${f.district}` })
      if (f.call_type) chips.push({ key: 'call_type', label: `Call Type: ${this.labelFor(this.callTypeOptions, f.call_type)}` })
      if (f.call_status) chips.push({ key: 'call_status', label: `Call Status: ${this.labelFor(this.callStatusOptions, f.call_status)}` })
      if (f.activity) chips.push({ key: 'activity', label: this.labelFor(this.activityOptions, f.activity) })
      if (f.bulk) chips.push({ key: 'bulk', label: 'Bulk customers' })
      if (f.bottles_min || f.bottles_max) {
        const range = f.bottles_min && f.bottles_max
          ? `${f.bottles_min}–${f.bottles_max}`
          : (f.bottles_min ? `≥ ${f.bottles_min}` : `≤ ${f.bottles_max}`)
        chips.push({ key: 'bottles', label: `Bottles: ${range}` })
      }
      if (f.date_from || f.date_to) {
        const basis = this.labelFor(this.dateBasisOptions, f.date_basis)
        const range = f.date_from && f.date_to
          ? `${f.date_from} → ${f.date_to}`
          : (f.date_from ? `From ${f.date_from}` : `Until ${f.date_to}`)
        chips.push({ key: 'date', label: `${basis}: ${range}` })
      }
      return chips
    },
    pageRangeLabel () {
      const total = this.total
      if (!this.rows.length) return '0 customers'
      if (!total) return `${this.rows.length} on page`
      const start = (this.currentPage - 1) * PER_PAGE + 1
      const end = Math.min(start + this.rows.length - 1, total)
      return `${start}–${end} of ${total} customer${total === 1 ? '' : 's'}`
    }
  },
  created () {
    this.fetchOptions()
    this.fetchCustomers(1)
  },
  methods: {
    token () {
      return localStorage.getItem('api_token')
    },
    labelFor (opts, value) {
      const found = (opts || []).find((o) => String(o.value) === String(value))
      return found ? found.label : value
    },
    initials (name) {
      if (!name) return '?'
      const parts = String(name).trim().split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const second = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + second).toUpperCase() || '?'
    },
    avatarColor (row) {
      const palette = [
        'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
        'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        'linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)',
        'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
        'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
        'linear-gradient(135deg, #115e59 0%, #14b8a6 100%)'
      ]
      const seed = String(row?.name || row?.id || row?.phone || '')
      let hash = 0
      for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      }
      return palette[hash % palette.length]
    },
    districtLabel (row) {
      const addresses = Array.isArray(row?.address) ? row.address : []
      const address = addresses[0] || null
      const district = address?.district || ''
      const state = address?.state || ''
      const parts = [district, state].map((p) => String(p || '').trim()).filter(Boolean)
      return parts.length ? parts.join(', ') : ''
    },
    customerTypeLabel (row) {
      if (!row?.customer_type) return ''
      return this.labelFor(this.customerTypeOptions, row.customer_type)
    },
    cropChips (row, limit = 3) {
      const crops = Array.isArray(row?.crops) ? row.crops : []
      const names = crops.map((c) => c?.crop?.name).filter(Boolean)
      const unique = Array.from(new Set(names))
      return limit === 99 ? unique : unique.slice(0, limit)
    },
    animalChips (row, limit = 2) {
      const animals = Array.isArray(row?.animals) ? row.animals : []
      const names = animals.map((a) => a?.animal).filter(Boolean)
      const unique = Array.from(new Set(names))
      return limit === 99 ? unique : unique.slice(0, limit)
    },
    hasAnyTag (row) {
      return !!this.customerTypeLabel(row) ||
        Number(row?.bulk_orders_count) > 0 ||
        this.cropChips(row, 99).length > 0 ||
        this.animalChips(row, 99).length > 0
    },
    extraTagCount (row) {
      const shownCrops = this.cropChips(row).length
      const shownAnimals = this.animalChips(row).length
      const totalCrops = this.cropChips(row, 99).length
      const totalAnimals = this.animalChips(row, 99).length
      return (totalCrops - shownCrops) + (totalAnimals - shownAnimals)
    },
    ordersCount (row) {
      if (typeof row?.orders_count === 'number') return row.orders_count
      if (Array.isArray(row?.order)) return row.order.length
      return Number(row?.orders_count || 0)
    },
    formatCurrency (value) {
      const numeric = Number(value || 0)
      return Number.isFinite(numeric) ? `₹${numeric.toFixed(2)}` : '₹0.00'
    },
    formatLabel (value) {
      return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    paymentStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s === 'paid') return 'tc-text-success'
      if (s.includes('pending')) return 'tc-text-warning'
      return ''
    },
    // Order.created_at is a Unix timestamp (the "1970" trap); prefer the ISO
    // order_date, fall back to created_at × 1000.
    orderDateDisplay (order) {
      if (!order) return '—'
      const fmt = (v) => {
        const d = new Date(v)
        if (isNaN(d.getTime())) return '—'
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
      }
      if (order.order_date) return fmt(order.order_date)
      const ts = Number(order.created_at)
      if (Number.isFinite(ts) && ts > 0) return fmt(ts * 1000)
      return '—'
    },
    // Call.created_at is cast as 'timestamp' on the model, so the API
    // serialises it as Unix SECONDS (a bare integer) — feeding that to
    // new Date() reads it as milliseconds and lands on 1970. Detect the
    // numeric/seconds case and convert to ms; otherwise parse as a string.
    formatCallDate (raw) {
      if (!raw && raw !== 0) return '—'
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const fmt = (v) => {
        const d = new Date(v)
        if (isNaN(d.getTime())) return null
        return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
      }
      const ts = Number(raw)
      if (Number.isFinite(ts) && ts > 0) {
        // Seconds → ms (10-digit Unix seconds); already-ms values pass through.
        return fmt(ts < 1e12 ? ts * 1000 : ts) || '—'
      }
      return fmt(raw) || String(raw)
    },
    goToTelesales (phone) {
      const number = String(phone || '').trim()
      if (!number) return
      this.$router.push({ path: '/telesales', query: { phone: number } })
    },
    async copyPhone (phone, id) {
      const number = String(phone || '').trim()
      if (!number) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(number)
        } else {
          const ta = document.createElement('textarea')
          ta.value = number
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
      } catch (e) {
        console.log(e)
        return
      }
      this.copiedId = id != null ? id : number
      if (this.copiedTimer) clearTimeout(this.copiedTimer)
      this.copiedTimer = setTimeout(() => { this.copiedId = null }, 1800)
    },
    openDetail (customer) {
      if (!customer) return
      this.detailCustomer = customer
      this.detailOrders = []
      this.detailCalls = []
      this.showDetailModal = true
      this.fetchDetail(customer.id)
    },
    closeDetail () {
      this.showDetailModal = false
      this.detailCustomer = null
      this.detailOrders = []
      this.detailCalls = []
    },
    fetchDetail (id) {
      if (!id) return
      this.detailLoading = true
      const params = { token: this.token() }
      Promise.all([
        axios.get(`${this.$apiAdress}/api/contacts/orders/${id}`, { params }),
        axios.get(`${this.$apiAdress}/api/contacts/calls/${id}`, { params })
      ])
        .then(([ordersRes, callsRes]) => {
          const ordersPayload = ordersRes.data || {}
          const callsPayload = callsRes.data || {}
          this.detailOrders = Array.isArray(ordersPayload.data) ? ordersPayload.data : (Array.isArray(ordersPayload) ? ordersPayload : [])
          this.detailCalls = Array.isArray(callsPayload.data) ? callsPayload.data : (Array.isArray(callsPayload) ? callsPayload : [])
        })
        .catch((error) => {
          console.log(error)
          this.detailOrders = []
          this.detailCalls = []
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    reload () {
      this.fetchCustomers(this.currentPage || 1)
    },
    queryParams () {
      const params = { token: this.token() }
      const f = this.applied
      if (f.search) params.search = f.search
      if (f.customer_type) params.customer_type = f.customer_type
      if (f.crop) params.crop = f.crop
      if (f.animal) params.animal = f.animal
      if (f.product) params.product = f.product
      if (f.state) params.state = f.state
      if (f.district) params.district = f.district
      if (f.call_type) params.call_type = f.call_type
      if (f.call_status) params.call_status = f.call_status
      if (f.activity) params.activity = f.activity
      if (f.bulk) params.bulk = f.bulk
      if (f.bottles_min !== '') params.bottles_min = f.bottles_min
      if (f.bottles_max !== '') params.bottles_max = f.bottles_max
      if (f.date_from) params.date_from = f.date_from
      if (f.date_to) params.date_to = f.date_to
      if ((f.date_from || f.date_to) && f.date_basis) params.date_basis = f.date_basis
      return params
    },
    fetchOptions () {
      axios.get(`${this.$apiAdress}/api/crm/customers/filter-options`, { params: { token: this.token() } })
        .then((response) => {
          const payload = response.data || {}
          this.options = {
            states: Array.isArray(payload.states) ? payload.states : [],
            districts: Array.isArray(payload.districts) ? payload.districts : [],
            crops: Array.isArray(payload.crops) ? payload.crops : [],
            animals: Array.isArray(payload.animals) ? payload.animals : [],
            products: Array.isArray(payload.products) ? payload.products : [],
            customer_types: Array.isArray(payload.customer_types) ? payload.customer_types : [],
            call_types: Array.isArray(payload.call_types) ? payload.call_types : [],
            call_statuses: Array.isArray(payload.call_statuses) ? payload.call_statuses : []
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },
    fetchCustomers (page) {
      this.errorMessage = ''
      this.isLoading = true
      const params = { ...this.queryParams(), page: page || 1 }
      axios.get(`${this.$apiAdress}/api/crm/customers`, { params })
        .then((response) => {
          const payload = response.data || {}
          const data = Array.isArray(payload.data) ? payload.data : []
          this.rows = data
          this.currentPage = Number(payload.current_page || page || 1)
          this.lastPage = Number(payload.last_page || 1)
          this.total = Number(payload.total || data.length)
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to load customers.'
          this.rows = []
          this.total = 0
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    syncApplied () {
      this.applied = {
        search: this.searchTerm,
        customer_type: this.filters.customer_type,
        crop: this.filters.crop,
        animal: this.filters.animal,
        product: this.filters.product,
        state: this.filters.state,
        district: this.filters.district,
        call_type: this.filters.call_type,
        call_status: this.filters.call_status,
        activity: this.filters.activity,
        bulk: this.filters.bulk,
        bottles_min: this.filters.bottles_min,
        bottles_max: this.filters.bottles_max,
        date_from: (this.dateRange && this.dateRange.fromDate) || '',
        date_to: (this.dateRange && this.dateRange.toDate) || '',
        date_basis: this.filters.date_basis
      }
    },
    onSearchSubmit () {
      this.syncApplied()
      this.fetchCustomers(1)
    },
    applyFilters () {
      this.syncApplied()
      this.fetchCustomers(1)
    },
    removeChip (key) {
      if (key === 'date') {
        this.dateRange = { fromDate: '', toDate: '' }
        this.filters.date_basis = ''
        this.applied.date_from = ''
        this.applied.date_to = ''
        this.applied.date_basis = ''
      } else if (key === 'bottles') {
        this.filters.bottles_min = ''
        this.filters.bottles_max = ''
        this.applied.bottles_min = ''
        this.applied.bottles_max = ''
      } else {
        this.filters[key] = ''
        this.applied[key] = ''
      }
      this.fetchCustomers(1)
    },
    clearAll () {
      this.searchTerm = ''
      this.dateRange = { fromDate: '', toDate: '' }
      this.filters = {
        customer_type: '', crop: '', animal: '', product: '',
        state: '', district: '', call_type: '', call_status: '',
        activity: '', bulk: '', bottles_min: '', bottles_max: '', date_basis: ''
      }
      this.syncApplied()
      this.fetchCustomers(1)
    },
    onPageChange (page) {
      const target = Number(page || 1)
      if (target === this.currentPage) return
      this.currentPage = target
      this.fetchCustomers(target)
    }
  }
}
</script>

<style scoped>
.tc-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.tc-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.tc-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.tc-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.tc-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.tc-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.tc-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.tc-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.tc-toolbar__actions >>> .btn.tc-refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.tc-toolbar__actions >>> .btn.tc-refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.tc-toolbar__actions >>> .btn.tc-refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.tc-refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.tc-toolbar__actions >>> .btn.tc-refresh-btn:hover:not(:disabled) .tc-refresh-btn__icon {
  transform: rotate(60deg);
}

.tc-toolbar__actions >>> .btn.tc-refresh-btn.is-loading .tc-refresh-btn__icon {
  animation: tc-refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes tc-refresh-spin {
  to { transform: rotate(360deg); }
}

/* ── Alert ── */
.tc-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Summary tiles ── */
.tc-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.tc-tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  border-left-width: 4px;
}

.tc-tile--total { border-left-color: #1f2a44; }
.tc-tile--orders { border-left-color: #16a07a; }
.tc-tile--bottles { border-left-color: #6d28d9; }
.tc-tile--bulk { border-left-color: #b45309; }

.tc-tile__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.tc-tile__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2a44;
}

/* ── Section card ── */
.tc-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.tc-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.tc-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.tc-active-count {
  font-size: 0.72rem;
  font-weight: 600;
  color: #0e5b46;
  background: #e6f5ee;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
}

.tc-section__body {
  padding: 1rem;
}

/* ── Filters ── */
/* Filter row: flex-wrap per the design system (never a fixed-column grid —
   that stretches action buttons to a full column). Alignment across the two
   wrapped rows comes from a shared track width instead: every field is either
   one track or exactly two (2 tracks + the gap between them), so each row
   starts its items at the same x-positions. With flex-grow the rows sized
   themselves independently — 8 fields on row 1, 6 on row 2 — and no column
   lined up, which read as clutter. */
.tc-filters {
  --tc-col: 178px;
  --tc-gap: 0.85rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1.1rem var(--tc-gap);
  align-items: flex-end;
}

.tc-filter {
  display: flex;
  flex-direction: column;
  flex: 0 0 var(--tc-col);
  min-width: 0;
}

/* Two-track fields — the gap they straddle is reclaimed so they stay on grid. */
.tc-filter--search,
.tc-filter--range {
  flex-basis: calc(var(--tc-col) * 2 + var(--tc-gap));
}

.tc-filter--bottles {
  flex-basis: var(--tc-col);
}

/* Narrow viewports: tracks stop paying for themselves — go full width. */
@media (max-width: 700px) {
  .tc-filters {
    gap: 0.9rem;
  }

  .tc-filter,
  .tc-filter--search,
  .tc-filter--range,
  .tc-filter--bottles {
    flex-basis: 100%;
  }
}

.tc-filter-actions {
  flex: 1 0 100%;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-top: 0.35rem;
}

.tc-filter__label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.tc-apply-btn,
.tc-reset-btn {
  min-width: 95px;
  height: 36px;
  padding: 0 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.tc-apply-btn {
  border: 0;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.tc-apply-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.tc-reset-btn {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.tc-reset-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.tc-apply-btn:disabled,
.tc-reset-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.tc-apply-btn:focus,
.tc-reset-btn:focus { outline: none; }

.tc-apply-btn:focus-visible,
.tc-reset-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* ── Active filter chips ── */
.tc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.85rem;
}

.tc-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.3rem 0.2rem 0.6rem;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  font-size: 0.74rem;
  font-weight: 600;
  color: #334155;
}

.tc-chip__x {
  width: 17px;
  height: 17px;
  border: 0;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tc-chip__x:hover {
  background: #cbd5e1;
  color: #1f2a44;
}

.tc-chip__x:focus { outline: none; }
.tc-chip__x:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* ── Search ── */
.tc-search-wrap {
  position: relative;
  width: 100%;
}

.tc-search-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.tc-input {
  width: 100%;
  height: 36px;
  padding: 0.3rem 2.25rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.tc-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.tc-search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.tc-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

.tc-search-clear:focus { outline: none; }
.tc-search-clear:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* ── Bottle-count range ── */
.tc-bottle-range {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.tc-input--num {
  padding: 0.3rem 0.6rem;
  text-align: center;
  -moz-appearance: textfield;
}

.tc-input--num::-webkit-outer-spin-button,
.tc-input--num::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.tc-bottle-range__sep {
  color: #9ca3af;
  font-weight: 600;
  flex: 0 0 auto;
}

/* ── Table region ── */
.tc-table-region {
  padding: 0 1rem 0.5rem;
}

.tc-result-hint {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
}

/* ── Cells ── */
.tc-customer-cell {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.tc-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
}

.tc-customer-id { min-width: 0; }

.tc-customer-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.25;
}

.tc-customer-sub {
  font-size: 0.73rem;
  color: #6b7280;
  line-height: 1.4;
}

.tc-phone {
  font-size: 0.85rem;
  color: #1f2a44;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}

.tc-dim {
  color: #9ca3af;
  font-size: 0.82rem;
}

.tc-tagwrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  max-width: 320px;
}

/* ── Pills ── */
.tc-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.16rem 0.5rem;
  border-radius: 6px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.tc-pill--loc {
  background: #e0edff;
  color: #1e40af;
  text-transform: capitalize;
}

.tc-pill--type {
  background: #ede9fe;
  color: #6d28d9;
}

.tc-pill--bulk {
  background: #fef3c7;
  color: #b45309;
}

.tc-pill--crop {
  background: #dcfce7;
  color: #15803d;
  text-transform: capitalize;
}

.tc-pill--animal {
  background: #ccfbf1;
  color: #0f766e;
  text-transform: capitalize;
}

.tc-pill--more {
  background: #f1f5f9;
  color: #475569;
}

.tc-pill--call {
  background: #eef1f7;
  color: #4b5563;
  border-radius: 999px;
  font-weight: 600;
}

/* ── Row actions ── */
.tc-row-actions {
  display: inline-flex;
  gap: 0.4rem;
  justify-content: flex-end;
}

.tc-act {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 28px;
  padding: 0 0.6rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.tc-act svg { flex: 0 0 auto; }

.tc-act:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tc-act--view:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.tc-act--call:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f0fbf6;
}

.tc-act--copy { padding: 0 0.5rem; }

.tc-act--copy:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #1e40af;
  background: #eff6ff;
}

.tc-act:focus { outline: none; }
.tc-act::-moz-focus-inner { border: 0; }
.tc-act:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* ── Pagination ── */
.tc-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 0.25rem 0.25rem;
  flex-wrap: wrap;
}

.tc-pagination-info {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
}

/* ── Detail modal ── */
.tc-modal {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.tc-modal__panel {
  width: 100%;
  max-width: 660px;
  max-height: calc(100vh - 2.5rem);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
}

.tc-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1.1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.tc-modal__id {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  min-width: 0;
}

.tc-modal__avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
}

.tc-modal__id-text { min-width: 0; }

.tc-modal__name {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.tc-modal__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.2rem;
}

.tc-modal__phone {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
  font-variant-numeric: tabular-nums;
}

.tc-modal__close {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.tc-modal__close:hover {
  background: #f3f4f6;
  color: #1f2a44;
}

.tc-modal__close:focus { outline: none; }
.tc-modal__close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.tc-modal__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-bottom: 1px solid #eef1f7;
}

.tc-modal__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 0.95rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.tc-modal__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.tc-modal__btn--call {
  border: 0;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.tc-modal__btn--call:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.tc-modal__btn--copy {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.tc-modal__btn--copy:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #1e40af;
  background: #eff6ff;
}

.tc-modal__btn:focus { outline: none; }
.tc-modal__btn::-moz-focus-inner { border: 0; }
.tc-modal__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.tc-modal__profile {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
}

.tc-modal__profile-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.tc-modal__profile-key {
  flex: 0 0 64px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  padding-top: 0.15rem;
}

.tc-modal__profile-vals {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.tc-modal__body {
  padding: 1rem 1.1rem 1.1rem;
  overflow-y: auto;
  background: #fbfcfe;
}

.tc-modal__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2.5rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.tc-history-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.tc-history-col { min-width: 0; }

.tc-history-title {
  margin: 0 0 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.tc-history-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 0.2rem;
}

.tc-history-item {
  padding: 0.55rem 0.7rem;
  border: 1px solid #e6eaf3;
  border-radius: 8px;
  background: #ffffff;
}

.tc-history-item__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.tc-history-item__strong {
  font-size: 0.84rem;
  font-weight: 700;
  color: #1f2a44;
}

.tc-history-item__date {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
}

.tc-history-item__amount {
  font-size: 0.88rem;
  font-weight: 700;
  color: #0e5b46;
  white-space: nowrap;
}

.tc-history-item__sub {
  margin-top: 0.2rem;
  font-size: 0.76rem;
  color: #6b7280;
}

.tc-dot {
  margin: 0 0.25rem;
  color: #cbd5e1;
}

.tc-text-success {
  color: #0e5b46;
  font-weight: 600;
}

.tc-text-warning {
  color: #b45309;
  font-weight: 600;
}

.tc-history-empty {
  padding: 0.85rem;
  text-align: center;
  font-size: 0.8rem;
  color: #6b7280;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
}

.tc-modal-fade-enter-active,
.tc-modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.tc-modal-fade-enter,
.tc-modal-fade-leave-to {
  opacity: 0;
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .tc-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .tc-toolbar {
    align-items: flex-start;
  }

  .tc-summary {
    grid-template-columns: 1fr 1fr;
  }

  .tc-history-grid {
    grid-template-columns: 1fr;
  }

  .tc-tagwrap {
    max-width: none;
  }
}
</style>
